import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "../hooks/useTranslation";
import { supabase } from "../lib/supabase";
import { useNavigate, useLocation } from "react-router-dom";

export default function Header({ searchQuery = '', setSearchQuery, unreadCount = 0 }) {
  const { user, signOut } = useAuth();
  const { t } = useTranslation();
  const [avatarUrl, setAvatarUrl] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const searchContainerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Popular baby item searches
  const popularSearches = [
    "stroller", "car seat", "baby clothes", "toys", "crib", "high chair",
    "baby carrier", "diaper bag", "bottles", "swing", "playpen", "walker",
    "bouncer", "rocking chair", "baby monitor", "breast pump", "formula maker"
  ];

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('babyxchange-recent-searches');
    if (saved) {
      setRecentSearches(JSON.parse(saved));
    }
  }, []);

  // Touch outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showSuggestions]);

  // Generate search suggestions
  const generateSuggestions = (query) => {
    if (!query.trim()) {
      return [...recentSearches, ...popularSearches].slice(0, 8);
    }
    
    const filtered = [...popularSearches, ...recentSearches]
      .filter(item => item.toLowerCase().includes(query.toLowerCase()))
      .slice(0, 8);
    
    return filtered;
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    if (value.length > 0) {
      setSearchSuggestions(generateSuggestions(value));
      setShowSuggestions(true);
    } else {
      setSearchSuggestions(generateSuggestions(''));
      setShowSuggestions(true);
    }
  };

  // Handle search suggestion click
  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion);
    setShowSuggestions(false);
    
    // Save to recent searches
    const updated = [suggestion, ...recentSearches.filter(s => s !== suggestion)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('babyxchange-recent-searches', JSON.stringify(updated));
  };

  // Handle search submit
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Save search to recent searches
      const updated = [searchQuery, ...recentSearches.filter(s => s !== searchQuery)].slice(0, 5);
      setRecentSearches(updated);
      localStorage.setItem('babyxchange-recent-searches', JSON.stringify(updated));
      
      setShowSuggestions(false);
      // Navigate to browse page with search query
      navigate(`/browse?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  useEffect(() => {
    if (user) {
      if (user.user_metadata?.avatar_url) {
        setAvatarUrl(user.user_metadata.avatar_url);
      } else {
        supabase.from('profiles').select('avatar_url').eq('id', user.id).single().then(({ data }) => {
          if (data && data.avatar_url) setAvatarUrl(data.avatar_url);
        });
      }
    }
  }, [user]);

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getUserDisplayName = () => {
    if (user?.user_metadata?.name) return user.user_metadata.name;
    if (user?.user_metadata?.full_name) return user.user_metadata.full_name;
    if (user?.email) return user.email.split('@')[0];
    return 'User';
  };

  const getUserInitials = () => {
    const name = getUserDisplayName();
    return name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <header className="modern-header">
      <div className="container-center mx-auto px-4 py-4">
        {/* Compact Mobile Layout - Everything in One Row */}
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Logo - Compact on Mobile */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-sm sm:text-lg">👶</span>
            </div>
            <div className="text-sm sm:text-responsive-lg font-bold gradient-text hidden sm:block">
              BabyXchange
            </div>
          </div>
          
          {/* Enhanced Search Bar - Takes Most Space */}
          <div ref={searchContainerRef} className="flex-1 relative">
            <form onSubmit={handleSearchSubmit}>
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400">
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input 
                className="search-bar w-full py-2 px-3 pl-9 sm:pl-12 text-sm sm:text-base" 
                placeholder="Search..." 
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => setShowSuggestions(true)}
                autoComplete="off"
              />
            </form>
            
            {/* Search Suggestions Dropdown */}
            {showSuggestions && searchSuggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-lg border border-slate-200 z-50 max-h-60 sm:max-h-80 overflow-y-auto search-suggestions-mobile">
                {/* Mobile close button */}
                <div className="flex justify-between items-center p-2 border-b border-slate-100 sm:hidden">
                  <span className="text-xs text-slate-500 font-medium">Search Suggestions</span>
                  <button
                    onClick={() => setShowSuggestions(false)}
                    className="p-1 hover:bg-slate-100 rounded-full transition-colors"
                  >
                    <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                {searchSuggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    className="w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors flex items-center gap-3 first:rounded-t-xl last:rounded-b-xl sm:first:rounded-t-xl sm:last:rounded-b-xl"
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <span className="text-slate-700">{suggestion}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* User Actions - Compact on Mobile */}
          <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
            {/* User Avatar - Smaller on Mobile */}
            <button
              className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center text-white text-xs sm:text-sm font-semibold overflow-hidden border-2 border-blue-400 hover:shadow-lg transition-all"
              onClick={() => navigate(location.pathname === '/profile' ? '/browse' : '/profile')}
              title={location.pathname === '/profile' ? 'Back to Browse' : 'My Profile'}
              style={{ padding: 0 }}
            >
              {avatarUrl ? (
                <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                getUserInitials()
              )}
            </button>
            
            {/* User Name - Hidden on Mobile */}
            <div className="hidden sm:block">
              <p className="text-sm font-medium text-slate-700">{getUserDisplayName()}</p>
            </div>
            
            {/* Logout Button - More Prominent */}
            <button 
              className="flex items-center gap-1 px-2 py-1.5 sm:px-3 sm:py-2 text-slate-600 hover:text-slate-800 hover:bg-red-50 border border-transparent hover:border-red-200 rounded-lg transition-all duration-200 group" 
              onClick={handleLogout} 
              title="Sign out of BabyXchange"
            >
              <svg className="w-4 h-4 sm:w-4 sm:h-4 text-slate-500 group-hover:text-red-600 transition-colors flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H7a2 2 0 01-2-2V7a2 2 0 012-2h4a2 2 0 012 2v1" />
              </svg>
              <span className="text-xs font-medium hidden min-[400px]:inline">Logout</span>
            </button>
          </div>
        </div>
      </div>
      
    </header>
  );
}
